import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { showErrorToast } from '@Helpers';

import ConfirmationModal from '@Components/ConfirmationModal/confirmationModal';
import { Schedule } from '@Models';
import { bopsApi } from '@Network';
import { getConflictMessage, getErrorStatus } from '../constants';

interface DeleteScheduleDialogProps {
  schedule: Pick<Schedule, 'id' | 'name'> | null;
  onClose: () => void;
  onDeleted: () => Promise<void> | void;
}

const DeleteScheduleDialog = ({ schedule, onClose, onDeleted }: DeleteScheduleDialogProps) => {
  const { t, i18n } = useTranslation();
  const [isDeleting, setIsDeleting] = useState(false);

  const handleConfirm = async () => {
    if (!schedule || isDeleting) return;
    setIsDeleting(true);
    try {
      await bopsApi.deleteSchedule(schedule.id);
    } catch (error) {
      if (getErrorStatus(error) !== 404) {
        showErrorToast(getConflictMessage(error, t, i18n, 'Schedule.Delete.Error'));
        setIsDeleting(false);
        onClose();
        return;
      }
    }
    onClose();
    try {
      await onDeleted();
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <ConfirmationModal
      isOpen={schedule !== null && !isDeleting}
      onClose={onClose}
      onConfirm={() => {
        handleConfirm().catch(() => {});
      }}
      confirmText={schedule ? t('Schedule.Delete.Confirm', { name: schedule.name }) : ''}
      buttonNamePrefix="SCHEDULE_DELETE"
    />
  );
};

export default DeleteScheduleDialog;
